import React, {useState} from 'react'
import {Box, Paper, Stack, Typography} from '@mui/material'
import {TabContext, TabList, TabPanel} from '@mui/lab'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined'

import PageTitle from '../../components/PageTitle'
import {StyledTab, FormHeaderIconBox} from './styled'

import UploadWizard from './UploadWizard'
import PdUploadWizard from './PdUploadWizard'
import AccountCodesTable from './AccountCodesTable'
import AuditLog from './AuditLog'
import {useParentCps, SHIPMENT_TYPE_B2B} from './useBulkServiceability'

// shipmentType 'B2B': the same screen for PTL couriers (PTL Operations → PTL Serviceability): B2B couriers only,
// pickup + drop lists, no SDD/NDD tab and no L1 settings. Left out = Bulk Serviceability as before.
export default function BulkServiceability({ embedded = false, shipmentType }) {
  const [tab, setTab] = useState('upload')
  const [refreshKey, setRefreshKey] = useState(0)
  const {parentCps} = useParentCps(shipmentType)
  const isB2b = shipmentType === SHIPMENT_TYPE_B2B

  const handleUploadComplete = () => {
    setRefreshKey((k) => k + 1)
  }

  return (
    <>
      {!embedded && <PageTitle>{isB2b ? 'PTL Serviceability' : 'Bulk Serviceability'}</PageTitle>}

      {/* Page intro band — picks up the same icon-box treatment used by FormHeader so the page reads as one unit */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 4,
          border: '1px solid #eeeeee',
          backgroundColor: '#fafafa',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <FormHeaderIconBox sx={{width: 40, height: 40, borderRadius: 2}}>
          <RouteOutlinedIcon fontSize='small' />
        </FormHeaderIconBox>
        <Stack sx={{flex: 1, minWidth: 0}}>
          <Typography sx={{fontWeight: 700, fontSize: '0.95rem', color: 'grey.800', lineHeight: 1.25}}>
            {isB2b ? 'PTL Pincode Serviceability' : 'Pincode-Level Serviceability'}
          </Typography>
          <Typography sx={{fontSize: '0.75rem', color: 'grey.600', mt: 0.25}}>
            {isB2b
              ? 'Upload pickup and drop pincodes for PTL couriers. B2B allocation books a courier only where both lists allow the lane.'
              : 'Upload pincode coverage for parent couriers, manage L1 toggles per account code, and audit every change.'}
          </Typography>
        </Stack>
        {parentCps?.length > 0 && (
          <Box
            sx={{
              flexShrink: 0,
              borderRadius: 2,
              backgroundColor: '#fff',
              border: '1px solid #eeeeee',
              px: 1.25,
              py: 0.5,
              textAlign: 'center',
              minWidth: 88,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.6rem',
                fontWeight: 700,
                color: 'grey.600',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Parent CPs
            </Typography>
            <Typography sx={{fontSize: '1.1rem', fontWeight: 800, color: 'grey.800', lineHeight: 1.1}}>
              {parentCps.length}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Tabs strip — wrapped in a Paper so it visually anchors above the tab body */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          borderRadius: 3,
          border: '1px solid #eeeeee',
          overflow: 'hidden',
        }}
      >
        <TabContext value={tab}>
          <TabList
            onChange={(_, v) => setTab(v)}
            sx={{
              px: 1,
              minHeight: 48,
              '& .MuiTabs-indicator': {height: 3, borderRadius: '3px 3px 0 0'},
            }}
          >
            <StyledTab
              icon={<UploadFileOutlinedIcon fontSize='small' />}
              iconPosition='start'
              label='Upload'
              value='upload'
            />
            {!isB2b && (
              <StyledTab
                icon={<UploadFileOutlinedIcon fontSize='small' />}
                iconPosition='start'
                label='SDD/NDD Upload'
                value='pd_upload'
              />
            )}
            <StyledTab
              icon={<ListAltOutlinedIcon fontSize='small' />}
              iconPosition='start'
              label='Account Codes'
              value='account_codes'
            />
            <StyledTab
              icon={<HistoryOutlinedIcon fontSize='small' />}
              iconPosition='start'
              label='Audit Log'
              value='audit_log'
            />
          </TabList>
        </TabContext>
      </Paper>

      {tab === 'upload' && (
        <UploadWizard parentCps={parentCps} onUploadComplete={handleUploadComplete} shipmentType={shipmentType} />
      )}
      {tab === 'pd_upload' && !isB2b && (
        <PdUploadWizard parentCps={parentCps} onUploadComplete={handleUploadComplete} />
      )}
      {tab === 'account_codes' && (
        <AccountCodesTable key={refreshKey} parentCps={parentCps} shipmentType={shipmentType} />
      )}
      {tab === 'audit_log' && <AuditLog parentCps={parentCps} shipmentType={shipmentType} />}
    </>
  )
}
